/**
 * Sidebar — chrome navigation principale de l'app ERP authentifiée.
 *
 * Présente sur tous les écrans authed (dashboard, vignoble, cuverie, ventes…).
 * L'item actif est calculé automatiquement depuis `useLocation()` :
 * - `/dashboard*` → "Tableau de bord"
 * - `/vignoble*`  → "Vignoble"
 * - etc.
 *
 * Si on veut afficher la sidebar avec un état "actif" forcé (ex : pour un
 * écran de prévisualisation), passer la prop `forceActivePath`.
 */

import {
  BarChart3,
  Building2,
  CalendarClock,
  FlaskConical,
  Grape,
  HelpCircle,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingBasket,
  Store,
  Truck,
  Warehouse,
  Wine,
} from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { useActivitesDomaine } from "../../auth/use-activites-domaine.js";
import { useCanAny } from "../../auth/use-can.js";
import { prefetchRoute } from "../../lib/route-prefetch.js";
import { LIBELLE_VERSION_BUILD } from "../../lib/version-build.js";

interface NavItemDef {
  /** Chemin associé (utilisé pour `to=` du Link et le calcul de l'actif). */
  path: string;
  label: string;
  icon: ReactNode;
}

interface NavSection {
  /** Libellé de la section (Pilotage, Production, Commerce, Cadre). Vide
   *  pour la dernière section "réglages" sans titre. */
  label?: string;
  items: NavItemDef[];
}

// La nav ERP MVP — alignée sur la roadmap V0
const NAV_SECTIONS: NavSection[] = [
  {
    label: "Pilotage",
    items: [{ path: "/dashboard", label: "Tableau de bord", icon: <LayoutDashboard size={18} /> }],
  },
  {
    label: "Production",
    items: [
      { path: "/vignoble", label: "Vignoble", icon: <Grape size={18} /> },
      { path: "/vendange", label: "Vendange", icon: <ShoppingBasket size={18} /> },
      { path: "/cuverie", label: "Cuverie", icon: <FlaskConical size={18} /> },
      { path: "/cave", label: "Cave et bouteilles", icon: <Wine size={18} /> },
      { path: "/stock", label: "Stocks", icon: <Warehouse size={18} /> },
    ],
  },
  {
    label: "Commerce",
    items: [
      { path: "/ventes", label: "Ventes", icon: <ShoppingBag size={18} /> },
      { path: "/expedition", label: "Expédition", icon: <Truck size={18} /> },
    ],
  },
  {
    // Gestion opérationnelle de la main-d'œuvre : missions/chantiers, suivi du
    // temps, coûts de production, planning/calendrier. À ne pas confondre avec
    // « Paramètres › Mon équipe » qui gère les comptes/rôles d'accès au logiciel.
    label: "Organisation",
    items: [{ path: "/equipes", label: "Équipes & planning", icon: <CalendarClock size={18} /> }],
  },
  {
    label: "Cadre",
    items: [
      { path: "/conformite", label: "Conformité", icon: <ShieldCheck size={18} /> },
      { path: "/statistiques", label: "Statistiques", icon: <BarChart3 size={18} /> },
    ],
  },
  // Section réglages : pas de titre, séparée par une ligne dans le rendu
  {
    items: [
      // Page index /parametres : 3 cartes (Mon domaine, Mon équipe,
      // Rôles & permissions). Les 2 dernières masquées si l'user n'a pas
      // les perms `membres.read` / `roles.read`.
      { path: "/parametres", label: "Paramètres", icon: <Settings size={18} /> },
      { path: "/aide", label: "Aide", icon: <HelpCircle size={18} /> },
    ],
  },
];

export interface SidebarProps {
  /** Nom commercial affiché dans le header sidebar (par défaut Crayéa). */
  brandName?: string;
  /** Plan d'abonnement affiché sous le brand (Cuvée / Tirage / Maison…). */
  plan?: string;
  /**
   * Données du domaine pour le footer carte abonnement.
   * `surface` reste optionnelle tant que la table parcelles/surfaces n'est
   * pas en place — on affiche juste le nom + plan dans ce cas.
   */
  domaine?: {
    nom: string;
    surface?: string;
  };
  /** Forcer le path actif (utile pour previews) ; sinon dérivé de `useLocation()`. */
  forceActivePath?: string;
}

export function Sidebar({
  brandName = "Crayéa",
  plan = "Cuvée",
  domaine,
  forceActivePath,
}: SidebarProps) {
  const location = useLocation();
  const activePath = forceActivePath ?? location.pathname;
  const { activites, visible } = useActivitesDomaine();
  const { granted: peutConfigurerActivites } = useCanAny(["domaine.update"]);

  // Entrée « Boutique » (Webshop backoffice) : affichée seulement si l'utilisateur
  // peut configurer la boutique OU voir ses commandes. Le back gate de toute façon
  // chaque endpoint (`tenantProcedureWithPermission`) — ce masquage est purement UX.
  const { granted: peutVoirBoutique } = useCanAny(["webshop.config", "webshop.commandes_read"]);
  const { granted: peutVoirStocks } = useCanAny([
    "stock.read",
    "stock.matieres_seches_read",
    "stock.inventaire_read",
    "stock.agricole_read",
  ]);
  const sections = useMemo<NavSection[]>(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: [
          ...section.items.filter(
            (item) => visible(item.path) && (item.path !== "/stock" || peutVoirStocks),
          ),
          ...(section.label === "Commerce" && peutVoirBoutique && visible("/boutique-admin")
            ? [{ path: "/boutique-admin", label: "Boutique", icon: <Store size={18} /> }]
            : []),
        ],
      })).filter((section) => section.items.length > 0),
    [peutVoirBoutique, peutVoirStocks, visible],
  );

  return (
    <aside className="dashboard-sidebar flex h-full w-[240px] shrink-0 flex-col">
      {/* En-tête : logo monogramme + nom commercial + plan */}
      <div className="px-5 pb-5 pt-6">
        <div className="flex items-center gap-2.5">
          <span className="dashboard-logo-mark" aria-hidden="true">
            cé
          </span>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold tracking-tight">{brandName}</div>
            <div className="mono mt-0.5 text-[10px] uppercase tracking-[0.14em] text-champagne-300/60">
              Plan {plan}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation principale */}
      <nav className="flex-1 overflow-y-auto px-4 pb-4" aria-label="Navigation principale">
        {!activites && peutConfigurerActivites && (
          <Link
            to="/parametres/domaine"
            className="mb-3 flex min-h-12 items-center rounded-lg px-3 text-xs text-champagne-200 hover:bg-white/10"
          >
            Adapter les menus à mes activités
          </Link>
        )}
        {sections.map((section, sIdx) => (
          // La dernière section "réglages" n'a pas de label → fallback "settings".
          // La liste est statique, pas de risque de collision.
          <div key={section.label ?? "settings"}>
            {/* Séparateur visuel pour la dernière section sans titre */}
            {!section.label && sIdx > 0 && (
              <div className="my-4 border-t border-champagne-300/20" aria-hidden="true" />
            )}
            {section.label && <div className="dashboard-nav-section">{section.label}</div>}
            {section.items.map((item) => (
              <SidebarLink
                key={item.path}
                path={item.path}
                label={item.label}
                icon={item.icon}
                active={isActive(activePath, item.path)}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Footer : carte domaine + lien gestion abonnement */}
      {domaine && (
        <div className="m-3 rounded-xl border border-champagne-300/20 bg-white/[0.04] p-3">
          <div className="flex items-start gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-champagne-300/25 bg-champagne-500/[0.18]">
              <Building2 size={16} className="text-champagne-300" />
            </div>
            <div className="leading-tight">
              <div className="text-[13px] font-semibold">{domaine.nom}</div>
              <div className="mt-0.5 text-[11px] text-champagne-50/55">
                {/* Surface masquée tant que pas saisie : on évite "Plan
                    Cuvée · " avec un · orphelin. */}
                Plan {plan}
                {domaine.surface ? ` · ${domaine.surface}` : ""}
              </div>
              <Link
                to="/parametres/abonnement"
                className="mt-1.5 inline-block text-[12px] font-medium text-champagne-300 hover:text-champagne-200"
              >
                Gérer l&apos;abonnement →
              </Link>
            </div>
          </div>
        </div>
      )}
      <div className="px-4 pb-3 text-center font-mono text-[10px] text-champagne-50/40">
        {LIBELLE_VERSION_BUILD}
      </div>
    </aside>
  );
}

// Item de navigation — utilise React Router pour le routing client
function SidebarLink({
  path,
  label,
  icon,
  active,
}: {
  path: string;
  label: string;
  icon: ReactNode;
  active: boolean;
}) {
  return (
    <Link
      to={path}
      className={`dashboard-nav-item ${active ? "active" : ""}`}
      aria-current={active ? "page" : undefined}
      // Prefetch du chunk de la section au survol / focus : au clic, le module
      // est déjà chargé → navigation instantanée, pas de flash du shell.
      //
      // `onPointerDown` est indispensable sur écran tactile : ni `mouseenter`
      // ni `focus` ne surviennent avant le tap, donc le préchargement était
      // purement décoratif en mobile — précisément là où le réseau est mauvais
      // et où le gain compte le plus (cuverie en 4G dégradée).
      onMouseEnter={() => prefetchRoute(path)}
      onFocus={() => prefetchRoute(path)}
      onPointerDown={() => prefetchRoute(path)}
    >
      <span className="opacity-90" aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
    </Link>
  );
}

/** Les routes historiques restent valides et allument leur espace parent. */
function isActive(currentPath: string, itemPath: string): boolean {
  const dans = (prefixe: string) =>
    currentPath === prefixe || currentPath.startsWith(`${prefixe}/`);
  if (dans("/stock-cols")) return itemPath === "/stock";
  if (dans("/etiquetage")) return itemPath === "/cave";
  return itemPath === "/dashboard" ? currentPath === "/" || dans(itemPath) : dans(itemPath);
}
